// =============================================================================
// Fuse.js Configuration for Smart Parsing (Q-304)
// =============================================================================

import type { IFuseOptions } from 'fuse.js';
import type { Cliente, Contacto, TipoActividadRef } from '@/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type MatchConfidence = 'high' | 'suggest' | 'none';

export interface SmartMatch<T = unknown> {
  item: T;
  score: number;
  confidence: MatchConfidence;
}

export interface SmartDetection {
  clienteRef?: string;
  clienteId?: string;
  clienteMatch?: SmartMatch<Cliente>;
  contactoRef?: string;
  contactoId?: string;
  contactoMatch?: SmartMatch<Contacto>;
  tipoActividadId?: string;
  tipoActividadRef?: string;
  tipoActividadMatch?: SmartMatch<TipoActividadRef>;
  horaAgendada?: string;
  fechaAgendada?: Date;
  area?: 'TRABAJO' | 'ADMINISTRATIVO' | 'PERSONAL';
  /** Tokens consumed by smart detection — to clean from titulo */
  consumedTokens: string[];
}

// ---------------------------------------------------------------------------
// Fuse.js Configuration per catalog
// ---------------------------------------------------------------------------

export const FUSE_CONFIG = {
  cliente: {
    threshold: 0.2,
    includeScore: true,
    minMatchCharLength: 4,
    keys: [{ name: 'nombre' as const, weight: 1.0 }],
  } satisfies IFuseOptions<Cliente>,
  contacto: {
    threshold: 0.3,
    includeScore: true,
    minMatchCharLength: 3,
    keys: [{ name: 'nombre' as const, weight: 1.0 }],
  } satisfies IFuseOptions<Contacto>,
  tipoActividad: {
    threshold: 0.3,
    includeScore: true,
    minMatchCharLength: 3,
    keys: [
      { name: 'nombre' as const, weight: 0.7 },
      { name: 'codigo' as const, weight: 0.8 },
      { name: 'aliases' as const, weight: 1.0 },
    ],
  } satisfies IFuseOptions<TipoActividadRef>,
};

// ---------------------------------------------------------------------------
// Confidence thresholds
// ---------------------------------------------------------------------------

export const MATCH_CONFIDENCE = {
  HIGH: 0.15,
  SUGGEST: 0.3,
} as const;

export function classifyConfidence(score: number): MatchConfidence {
  if (score < MATCH_CONFIDENCE.HIGH) return 'high';
  if (score < MATCH_CONFIDENCE.SUGGEST) return 'suggest';
  return 'none';
}
